import { X } from "@ews-admin/global-design-system";
import React, { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  features: string[];
  onChange: (features: string[]) => void;
}

export const FeatureListEditor: React.FC<Props> = ({ features, onChange }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");

  const add = () => {
    const trimmed = input.trim();
    if (!trimmed || features.includes(trimmed)) return;
    onChange([...features, trimmed]);
    setInput("");
  };

  const remove = (index: number) => {
    onChange(features.filter((_, i) => i !== index));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="space-y-2">
      <ul className="space-y-1">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-800"
          >
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
              {f}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
              aria-label={t("common.remove")}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
        {features.length === 0 && (
          <li className="text-sm text-gray-400 italic px-1">
            {t("admin.planEditor.noFeatures")}
          </li>
        )}
      </ul>

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={t("admin.planEditor.addFeaturePlaceholder")}
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={add}
          disabled={!input.trim()}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t("common.add")}
        </button>
      </div>
    </div>
  );
};

export function featuresToString(features: string[]): string | null {
  return features.length > 0 ? features.join("\n") : null;
}

export function stringToFeatures(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split("\n")
    .map(f => f.trim())
    .filter(Boolean);
}
